/**
 * Renders a completed Customer Sign-Off to a PDF (returned as a base64 data URI).
 * Used when the client submits: the PDF is stored server-side and emailed to the
 * client, billing, and the order creator.
 */

import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import {
  ACCEPTANCE_ITEMS,
  TRAINING_TOPICS,
  VERIFICATION_TASKS,
  type CustomerSignoffFormData,
} from '../data/customerSignoffForm';

const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 48;
const CONTENT_W = PAGE_W - MARGIN * 2;

const NAVY = rgb(0.16, 0.26, 0.37);
const GREY = rgb(0.35, 0.44, 0.55);
const BLACK = rgb(0.1, 0.11, 0.12);
const LINE = rgb(0.8, 0.83, 0.87);

export async function generateCustomerSignoffPdf(
  form: CustomerSignoffFormData,
  meta: { businessName: string }
): Promise<string> {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);

  let page: PDFPage = doc.addPage([PAGE_W, PAGE_H]);
  let y = PAGE_H - MARGIN;

  const ensure = (needed: number) => {
    if (y - needed < MARGIN) {
      page = doc.addPage([PAGE_W, PAGE_H]);
      y = PAGE_H - MARGIN;
    }
  };

  const wrap = (text: string, f: PDFFont, size: number, maxW: number): string[] => {
    const words = String(text ?? '').split(/\s+/).filter(Boolean);
    if (words.length === 0) return [''];
    const lines: string[] = [];
    let cur = '';
    for (const w of words) {
      const test = cur ? `${cur} ${w}` : w;
      if (f.widthOfTextAtSize(test, size) > maxW && cur) {
        lines.push(cur);
        cur = w;
      } else {
        cur = test;
      }
    }
    if (cur) lines.push(cur);
    return lines;
  };

  const text = (
    s: string,
    opts: { x?: number; size?: number; f?: PDFFont; color?: ReturnType<typeof rgb>; maxW?: number } = {}
  ) => {
    const size = opts.size ?? 10;
    const f = opts.f ?? font;
    const x = opts.x ?? MARGIN;
    const maxW = opts.maxW ?? CONTENT_W - (x - MARGIN);
    const lines = wrap(s, f, size, maxW);
    for (const line of lines) {
      ensure(size + 4);
      page.drawText(line, { x, y: y - size, size, font: f, color: opts.color ?? BLACK });
      y -= size + 4;
    }
  };

  const heading = (s: string) => {
    ensure(28);
    y -= 8;
    page.drawText(s, { x: MARGIN, y: y - 12, size: 12, font: bold, color: NAVY });
    y -= 16;
    ensure(2);
    page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 0.75, color: LINE });
    y -= 8;
  };

  const kv = (label: string, value: string) => {
    ensure(14);
    const labelW = 150;
    page.drawText(label, { x: MARGIN, y: y - 10, size: 10, font: bold, color: GREY });
    const lines = wrap(value || '—', font, 10, CONTENT_W - labelW);
    lines.forEach((line, idx) => {
      if (idx > 0) ensure(12);
      page.drawText(line, { x: MARGIN + labelW, y: y - 10, size: 10, font, color: BLACK });
      y -= idx < lines.length - 1 ? 12 : 0;
    });
    y -= 14;
  };

  /** Simple table: header row + body rows, fixed column x/width. */
  const table = (headers: string[], widths: number[], rows: string[][]) => {
    const rowH = 16;
    const colX: number[] = [];
    let cx = MARGIN;
    for (const w of widths) {
      colX.push(cx);
      cx += w;
    }
    ensure(rowH + 4);
    // Header
    headers.forEach((h, idx) => {
      page.drawText(h, { x: colX[idx] + 2, y: y - 11, size: 8.5, font: bold, color: GREY });
    });
    y -= rowH;
    page.drawLine({ start: { x: MARGIN, y: y + 4 }, end: { x: PAGE_W - MARGIN, y: y + 4 }, thickness: 0.5, color: LINE });
    // Body
    for (const row of rows) {
      // Height = tallest wrapped cell
      const cellLines = row.map((c, idx) => wrap(c || '', font, 8.5, widths[idx] - 4));
      const lineCount = Math.max(1, ...cellLines.map((l) => l.length));
      const h = lineCount * 11 + 5;
      ensure(h);
      for (let idx = 0; idx < cellLines.length; idx++) {
        const lines = cellLines[idx];
        for (let li = 0; li < lines.length; li++) {
          page.drawText(lines[li], { x: colX[idx] + 2, y: y - 10 - li * 11, size: 8.5, font, color: BLACK });
        }
      }
      y -= h;
      page.drawLine({ start: { x: MARGIN, y: y + 3 }, end: { x: PAGE_W - MARGIN, y: y + 3 }, thickness: 0.4, color: LINE });
    }
    y -= 4;
  };

  const drawSignature = async (label: string, rep: { name: string; title: string; signature: string; date: string }) => {
    ensure(90);
    page.drawText(label, { x: MARGIN, y: y - 11, size: 10, font: bold, color: NAVY });
    y -= 18;
    kv('Name', rep.name);
    kv('Title', rep.title);
    kv('Date', rep.date);
    // Signature image
    ensure(60);
    page.drawText('Signature', { x: MARGIN, y: y - 10, size: 10, font: bold, color: GREY });
    if (rep.signature && rep.signature.startsWith('data:image')) {
      try {
        const img = rep.signature.includes('image/jpeg')
          ? await doc.embedJpg(rep.signature)
          : await doc.embedPng(rep.signature);
        const maxW = 200;
        const maxH = 50;
        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = img.width * scale;
        const h = img.height * scale;
        page.drawImage(img, { x: MARGIN + 150, y: y - h, width: w, height: h });
        y -= Math.max(h, 14) + 6;
      } catch {
        page.drawText('(signature on file)', { x: MARGIN + 150, y: y - 10, size: 9, font, color: GREY });
        y -= 16;
      }
    } else {
      page.drawText('—', { x: MARGIN + 150, y: y - 10, size: 10, font, color: BLACK });
      y -= 16;
    }
    y -= 8;
  };

  // ── Title ──
  page.drawText('Customer Sign-Off', { x: MARGIN, y: y - 20, size: 18, font: bold, color: NAVY });
  y -= 26;
  page.drawText(meta.businessName || '', { x: MARGIN, y: y - 12, size: 11, font, color: GREY });
  y -= 22;

  // ── Project Information ──
  heading('Project Information');
  kv('Customer Name', form.project.customerName);
  kv('Site Name', form.project.siteName);
  kv('Site Address', form.project.siteAddress);
  kv('Deployment Date', form.project.deploymentDate);
  kv('Project Manager', form.project.projectManager);
  kv('Robotics Technician(s)', form.project.technicians);

  // ── Equipment Delivered ──
  heading('Equipment Delivered');
  table(
    ['Product', 'Qty Exp.', 'Qty Rec.', 'Condition', 'Initials'],
    [200, 55, 55, 90, 68],
    form.equipment.map((e) => [e.name, e.qtyExpected, e.qtyReceived, e.condition, e.clientInitials])
  );

  // ── Installation Verification ──
  heading('Installation Verification');
  table(
    ['Task', 'Completed'],
    [370, 98],
    VERIFICATION_TASKS.map((t) => [t.label, form.verification[t.id] || '—'])
  );

  // ── Training Completion ──
  heading('Training Completion');
  table(
    ['Training Topic', 'Completed', 'Initials'],
    [300, 90, 78],
    TRAINING_TOPICS.map((t) => {
      const entry = form.training.topics[t.id];
      return [t.label, entry?.done ? 'Yes' : 'No', entry?.initials || ''];
    })
  );
  kv('Training Conducted By', form.training.conductedByName);
  kv('Date', form.training.conductedByDate);

  // ── Client Acceptance Checklist ──
  heading('Client Acceptance Checklist');
  for (const item of ACCEPTANCE_ITEMS) {
    text(`${form.acceptance[item.id] ? '[X]' : '[ ]'}  ${item.label}`, { size: 9.5 });
  }

  // ── Outstanding Items / Punch List ──
  if (form.punchList.length > 0) {
    heading('Outstanding Items / Punch List');
    table(
      ['Item', 'Responsible Party', 'Target Completion'],
      [230, 140, 98],
      form.punchList.map((p) => [p.item, p.responsibleParty, p.targetDate])
    );
  }

  // ── Client Comments ──
  if ((form.clientComments || '').trim()) {
    heading('Client Comments');
    text(form.clientComments, { size: 9.5 });
  }

  // ── Final Acceptance ──
  heading('Final Acceptance');
  await drawSignature('Company Representative', form.finalAcceptance.company);
  await drawSignature('Client Representative', form.finalAcceptance.client);

  // ── Optional Follow-Up ──
  const fu = form.followUp;
  if (fu.thirtyDayScheduled || fu.supportContact || fu.supportPhone || fu.supportEmail) {
    heading('Optional Follow-Up');
    kv('30-Day Follow-Up', fu.thirtyDayScheduled);
    kv('Primary Support Contact', fu.supportContact);
    kv('Support Phone', fu.supportPhone);
    kv('Support Email', fu.supportEmail);
  }

  return doc.saveAsBase64({ dataUri: true });
}
